"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const stats = [{
  value: "30.000+",
  label: "Tevreden Klanten",
  description: "Jarenlange ervaring in het leveren van topkwaliteit."
}, {
  value: "2010",
  label: "Sinds Opgericht",
  description: "Een gevestigde naam in Middelharnis en omstreken."
}];
const usps = [{
  title: "Ervaren professionele barbers",
  description: "Ons team bestaat uit meesters in hun vak, met passie voor het barbiersvak."
}, {
  title: "Snelle en efficiënte service",
  description: "Wij respecteren uw tijd en zorgen voor een strak resultaat zonder onnodig wachten."
}, {
  title: "Moderne fades & klassiek",
  description: "Van de strakste skin fades tot tijdloze klassieke herenkapsels."
}, {
  title: "Vakkundige baardverzorging",
  description: "Precisie trimmen en traditionele hot towel scheerbeurten voor de perfecte baard."
}];
export default function WaaromKiezenVoorHE() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="waarom-kiezen-voor-he" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/vintage_barbershop_wall_texture.png)] bg-cover bg-center opacity-10 mix-blend-luminosity"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className={`lg:col-span-5 flex flex-col gap-12 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
                Waarom Kiezen Voor <span className="text-primary">H&E Kappers?</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Sinds 2010 combineren wij traditioneel vakmanschap met moderne technieken. 
                Ontdek waarom duizenden mannen in Goeree-Overflakkee ons vertrouwen voor hun uitstraling.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-8 sm:gap-12">
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col">
                  <span className="text-5xl md:text-6xl font-bold text-primary mb-2 tracking-tighter">
                    {stat.value}
                  </span>
                  <span className="text-xl font-semibold text-foreground mb-1">
                    {stat.label}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {stat.description}
                  </span>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="text-base font-semibold px-8">
                <Link to="https://salonkee.nl/salon/he-kappers" newTab>
                  Maak een afspraak
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 lg:pl-12 transition-all duration-1000 delay-300 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {usps.map((usp, i) => <div key={i} data-index={i} className="bg-card/50 border border-border/50 p-6 rounded-xl backdrop-blur-sm hover:bg-card/80 transition-colors duration-300">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 bg-primary/10 p-2 rounded-full shrink-0">
                      <CircleCheck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-2">
                        {usp.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {usp.description}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}